import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react';
import { directorsAPI } from '@/lib/api';
import { toast } from 'sonner';
import logger from '@/lib/logger';

export default function BulkImportDirectorsDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [createMissing, setCreateMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setSubmitting(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please pick a file');
      return;
    }
    setSubmitting(true);
    try {
      const res = await directorsAPI.bulkImport(file, {
        sheet_name: 'ROC',
        create_missing_clients: createMissing,
      });
      setResult(res.data);
      toast.success(`Imported: ${res.data.directors_created} new directors, ${res.data.links_created} new links`);
      onImported && onImported();
    } catch (err) {
      logger.error('Bulk import failed', err);
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" /> Bulk Import Directors
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="rounded-md border border-dashed bg-muted/20 p-4 text-xs text-muted-foreground space-y-1.5">
              <p className="font-medium text-foreground text-sm">Expected sheet format (sheet name: <code className="bg-muted px-1 rounded">ROC</code>):</p>
              <p>• Column B = <strong>Company Name</strong> (matched against existing clients)</p>
              <p>• Column C = <strong>Director Name</strong>, Column D = <strong>DIN</strong> (8 digits)</p>
              <p>• Column N = <strong>2nd Director Name</strong> (optional), Column O = <strong>2nd Director DIN</strong></p>
              <p className="pt-1">Existing directors (matched by DIN) and existing client links are reused.</p>
            </div>

            <div>
              <Label htmlFor="bulk-file" className="text-xs">Excel file (.xlsx)</Label>
              <input
                id="bulk-file"
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer"
                data-testid="bulk-import-file-input"
              />
              {file && <p className="text-[11px] text-muted-foreground mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
            </div>

            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <div className="pr-3">
                <p className="text-sm font-medium">Auto-create missing clients</p>
                <p className="text-[11px] text-muted-foreground">If a company in the sheet isn't found in your clients, create a stub client with the legal status auto-detected.</p>
              </div>
              <Switch checked={createMissing} onCheckedChange={setCreateMissing} data-testid="bulk-create-missing-toggle" />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => handleClose(false)} disabled={submitting}>Cancel</Button>
              <Button type="button" onClick={handleSubmit} disabled={!file || submitting} data-testid="bulk-import-submit">
                <Upload className="h-4 w-4 mr-1.5" />
                {submitting ? 'Importing…' : 'Import'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-emerald-900">Import complete</p>
                <p className="text-emerald-700 text-xs">Processed {result.rows_processed} row(s) from sheet ROC.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Stat label="Directors created" value={result.directors_created} accent="emerald" />
              <Stat label="Directors reused" value={result.directors_existed} />
              <Stat label="Linkages created" value={result.links_created} accent="emerald" />
              <Stat label="Linkages already existed" value={result.links_existed} />
              {result.clients_created > 0 && <Stat label="Stub clients created" value={result.clients_created} accent="amber" />}
              {result.skipped_no_din > 0 && <Stat label="Rows skipped (no/bad DIN)" value={result.skipped_no_din} accent="amber" />}
            </div>

            {result.fuzzy_matches?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Fuzzy matches ({result.fuzzy_matches.length})
                </Label>
                <p className="text-[11px] text-muted-foreground">Companies matched with minor name differences. Please verify these are correct in your DB.</p>
                <div className="rounded-md border max-h-40 overflow-y-auto">
                  {result.fuzzy_matches.map((f) => (
                    <div key={`fz-${f.row}-${f.from}`} className="px-3 py-1.5 text-xs border-b last:border-0 flex items-center justify-between gap-2">
                      <span className="truncate"><strong>"{f.from}"</strong> → <span className="text-muted-foreground">{f.matched_norm}</span></span>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{Math.round(f.score * 100)}%</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.companies_not_found?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" /> Companies not found ({result.companies_not_found.length})
                </Label>
                <p className="text-[11px] text-muted-foreground">These companies don't exist in your client list. Create them first, or re-run with "Auto-create missing clients" toggle on.</p>
                <div className="rounded-md border max-h-40 overflow-y-auto">
                  {result.companies_not_found.map((c) => (
                    <div key={`nf-${c.row}-${c.company}`} className="px-3 py-1.5 text-xs border-b last:border-0">
                      <span className="text-muted-foreground mr-2">Row {c.row}:</span>{c.company}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-red-500">Errors ({result.errors.length})</Label>
                <div className="rounded-md border border-red-200 bg-red-50 max-h-40 overflow-y-auto">
                  {result.errors.map((e) => (
                    <div key={`err-${e.row}-${e.reason}`} className="px-3 py-1.5 text-xs border-b last:border-0">Row {e.row}: {e.reason}</div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => reset()}>Import another file</Button>
              <Button type="button" onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, accent }) => {
  const accentMap = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
  };
  return (
    <div className={`rounded-md border p-2.5 ${accentMap[accent] || ''}`}>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
};
